import * as THREE from "three";
import { animateRunner } from "./world.js";

const PLAYER_RADIUS = 0.34;
const GRAVITY = -25;
const MOVE_SPEED = 7.2;
const AIR_CONTROL = 0.72;
const JUMP_SPEED = 12.2;
const COYOTE_TIME = 0.13;
const JUMP_BUFFER = 0.14;

export class PlayerController {
  constructor(mesh, world, callbacks = {}) {
    this.mesh = mesh;
    this.world = world;
    this.callbacks = callbacks;
    this.position = new THREE.Vector3();
    this.velocity = new THREE.Vector3();
    this.yaw = 0;
    this.cameraYaw = 0;
    this.grounded = false;
    this.groundPlatform = null;
    this.coyote = 0;
    this.jumpBuffer = 0;
    this.checkpointIndex = 0;
    this.currentPlatformIndex = 0;
    this.active = false;
    this.eliminated = false;
    this.finished = false;
    this.lastBounceAt = 0;
    this.collected = new Set();
  }

  spawn(platformIndex = 0) {
    const platform = this.world.course[platformIndex] ?? this.world.course[0];
    if (!platform) return;
    const transform = this.world.platformTransform(platform);
    this.position.set(transform.x, transform.y + 0.18, transform.z);
    this.velocity.set(0, 0, 0);
    this.checkpointIndex = platformIndex;
    this.currentPlatformIndex = platformIndex;
    this.grounded = true;
    this.groundPlatform = platform;
    this.eliminated = false;
    this.finished = false;
    this.mesh.visible = true;
    this.mesh.position.copy(this.position);
    const next = this.world.course[platformIndex + 1];
    this.cameraYaw = next
      ? Math.atan2(next.x - transform.x, next.z - transform.z)
      : 0;
    this.yaw = this.cameraYaw;
  }

  resetToCheckpoint() {
    this.spawn(this.checkpointIndex);
    this.callbacks.onRespawn?.();
  }

  update(input, delta, elapsed, waterHeight) {
    if (!this.active || this.eliminated || this.finished) {
      this.mesh.position.copy(this.position);
      return;
    }
    const dt = Math.min(delta, 0.034);
    const previousY = this.position.y;
    this.cameraYaw += input.look;

    // Three.js is right-handed: looking +Z makes camera-right -X.
    const forwardX = Math.sin(this.cameraYaw);
    const forwardZ = Math.cos(this.cameraYaw);
    const rightX = -Math.cos(this.cameraYaw);
    const rightZ = Math.sin(this.cameraYaw);
    const desiredX = rightX * input.x + forwardX * input.z;
    const desiredZ = rightZ * input.x + forwardZ * input.z;
    const moving = Math.hypot(desiredX, desiredZ) > 0.05;
    const control = this.grounded ? 15 : 7 * AIR_CONTROL;
    this.velocity.x += (desiredX * MOVE_SPEED - this.velocity.x) * Math.min(1, control * dt);
    this.velocity.z += (desiredZ * MOVE_SPEED - this.velocity.z) * Math.min(1, control * dt);
    if (!moving && this.grounded) {
      this.velocity.x *= Math.exp(-12 * dt);
      this.velocity.z *= Math.exp(-12 * dt);
    }

    if (moving) {
      const targetYaw = Math.atan2(this.velocity.x, this.velocity.z);
      this.yaw = lerpAngle(this.yaw, targetYaw, 1 - Math.exp(-dt * 14));
    }

    if (input.jump) this.jumpBuffer = JUMP_BUFFER;
    this.jumpBuffer = Math.max(0, this.jumpBuffer - dt);
    this.coyote = this.grounded ? COYOTE_TIME : Math.max(0, this.coyote - dt);
    if (this.jumpBuffer > 0 && this.coyote > 0) {
      this.velocity.y = JUMP_SPEED;
      this.grounded = false;
      this.groundPlatform = null;
      this.coyote = 0;
      this.jumpBuffer = 0;
      this.callbacks.onJump?.();
    }

    if (this.grounded && this.groundPlatform?.type === "moving") {
      const now = this.world.platformTransform(this.groundPlatform, elapsed);
      const before = this.world.platformTransform(this.groundPlatform, elapsed - dt);
      this.position.x += now.x - before.x;
      this.position.z += now.z - before.z;
    }

    this.velocity.y += GRAVITY * dt;
    this.position.addScaledVector(this.velocity, dt);
    this.grounded = false;
    this.groundPlatform = null;
    this.resolvePlatforms(previousY, elapsed);

    if (this.position.y < waterHeight + 0.5) {
      this.eliminated = true;
      this.active = false;
      this.mesh.visible = false;
      this.callbacks.onEliminated?.();
      return;
    }

    const checkpoint = this.world.course[this.checkpointIndex];
    if (checkpoint && this.position.y < checkpoint.y - 9 && checkpoint.y > waterHeight + 0.8) {
      this.resetToCheckpoint();
    }

    this.mesh.position.copy(this.position);
    this.mesh.rotation.y = this.yaw;
    animateRunner(
      this.mesh,
      elapsed,
      moving ? Math.hypot(this.velocity.x, this.velocity.z) / MOVE_SPEED : 0,
      this.grounded,
    );
  }

  resolvePlatforms(previousY, elapsed) {
    if (this.velocity.y > 0.2) return;
    let landing = null;
    let landingIndex = -1;
    let highestTop = -Infinity;

    this.world.course.forEach((platform, index) => {
      const transform = this.world.platformTransform(platform, elapsed);
      const top = transform.y + 0.13;
      const withinX = Math.abs(this.position.x - transform.x) <= platform.width / 2 + PLAYER_RADIUS * 0.35;
      const withinZ = Math.abs(this.position.z - transform.z) <= platform.depth / 2 + PLAYER_RADIUS * 0.35;
      const crossedTop = previousY >= top - 0.08 && this.position.y <= top + 0.16;
      if (withinX && withinZ && crossedTop && top > highestTop) {
        landing = platform;
        landingIndex = index;
        highestTop = top;
      }
    });

    if (!landing) return;
    this.position.y = highestTop;
    this.velocity.y = 0;
    this.grounded = true;
    this.groundPlatform = landing;
    this.currentPlatformIndex = Math.max(this.currentPlatformIndex, landingIndex);

    if (landing.type === "bounce" && elapsed - this.lastBounceAt > 0.3) {
      this.velocity.y = 13;
      this.grounded = false;
      this.groundPlatform = null;
      this.lastBounceAt = elapsed;
      this.callbacks.onBounce?.();
    }

    if (landing.type === "shelter" && landingIndex > this.checkpointIndex) {
      this.checkpointIndex = landingIndex;
      this.callbacks.onCheckpoint?.(landingIndex, landing.id);
    }

    if (landing.hasPickup && !this.collected.has(landing.id)) {
      this.collected.add(landing.id);
      this.callbacks.onPickup?.(landing.id);
    }

    if (landing.type === "finish" || landingIndex === this.world.course.length - 1) {
      this.finished = true;
      this.active = false;
      this.callbacks.onFinish?.(landing.id);
    }
  }

  snapshot() {
    return {
      x: round(this.position.x),
      y: round(this.position.y),
      z: round(this.position.z),
      yaw: round(this.yaw),
      platformIndex: this.currentPlatformIndex,
    };
  }
}

function round(value) {
  return Math.round(value * 1000) / 1000;
}

function lerpAngle(from, to, alpha) {
  const difference = Math.atan2(Math.sin(to - from), Math.cos(to - from));
  return from + difference * alpha;
}
